/**
 * Chain reference data for the /chains/* pages.
 *
 * Two layers:
 *  1. LIVE values (chain name, chain id, RPC, explorer, native token) are read
 *     from `src/chainData.json` at runtime, so a page can never drift from the
 *     network the app actually connects to.
 *  2. EDITORIAL content (consensus, tokenomics, ecosystem, tooling) lives in
 *     `CHAIN_ARTICLES`, written from each project's own documentation.
 *
 * chainData.json names the explorer key inconsistently (most rows use `exp`,
 * Coston2 uses `explorer`), so `explorerOf` accepts both. Do not rename those
 * keys in chainData.json — the wagmi layer reads both.
 *
 * Facts a project's own docs do not publish are omitted rather than guessed.
 *
 * @module data/chains
 * @category Data
 */

import { chainData } from '../chains'

/** Shape of a raw chainData.json row (fields vary between rows). */
export interface ChainRow {
  rpc?: string
  chainId?: string
  tokName?: string
  network?: string
  chainName?: string
  aManager?: string
  nftDeployer?: string
  bgti?: string
  MultiCall?: string
  PriceConverter?: string
  WNat?: string
  erc20Factory?: string
  /** Explorer URL. Rows use either `exp` or `explorer`. */
  exp?: string
  explorer?: string
  hidden?: boolean
}

/** Resolve a chainData row's explorer URL, tolerating both key names. */
export function explorerOf(row: ChainRow | undefined): string {
  if (!row) return ''
  return row.exp || row.explorer || ''
}

/** CAIP-2 namespace id for an EVM chain, e.g. "eip155:14". */
export function caipOf(row: ChainRow | undefined): string {
  return row?.chainId ? `eip155:${row.chainId}` : ''
}

/**
 * Look up the chainData row for a numeric chain id.
 *
 * chainData.json contains a duplicate chain id (16 appears twice, as "Coston 1"
 * and as hidden "Coston"), so the first match wins — that is the non-hidden row.
 */
export function chainDataFromId(chainId: number | string): ChainRow | undefined {
  const key = String(chainId)
  return (chainData as ChainRow[]).find((row) => row?.chainId === key)
}

/** A linked resource in a chain's links or per-section resources. */
export interface ChainLink {
  label: string
  href: string
}

/** Editorial content for one chain. Keyed to a chain id via `chainId`. */
export interface ChainArticle {
  /** Slug used in /chains/<slug>. Must match a route file name. */
  slug: string
  /** Chain id this article describes, as a number. */
  chainId: number
  /** Display name, e.g. "Flare". */
  name: string
  /** Short one-line positioning statement. */
  tagline: string
  /** 2-4 sentence overview rendered under the title. */
  intro: string
  /** True for testnets. Testnets get a visible warning banner. */
  testnet: boolean
  /** Sort weight for dropdown ordering; higher = earlier. */
  weight?: number
  /** UI-only flag: if true, render this chain under the testnet divider. */
  isTest?: boolean
  /** Consensus / performance / token facts. Not duplicated from chainData. */
  specs: Array<{ label: string; value: string }>
  /** Longer prose sections, in reading order. */
  sections: Array<{ heading: string; body: string[]; links?: ChainLink[] }>
  /** Outbound links (explorer, docs, faucet). */
  links: ChainLink[]
  /** Marketplace-specific note: how listings and settlement work here. */
  marketplaceNote?: string
}

/**
 * Editorial content, one entry per chain with a /chains/<slug> route.
 * Order matches the order chains appear in the Chains dropdown.
 */
export const CHAIN_ARTICLES: ChainArticle[] = [
  {
    slug: 'songbird',
    name: 'Songbird',
    chainId: 19,
    weight: 8,
    tagline: 'The original Flare network, now the canary network for protocol upgrades.',
    intro:
      'Songbird is the chain Flare was launched from, and it now serves as the canary network: the experimental network where protocol-level upgrades are tested, with real consequences for real value, before they are deployed to Flare. Transactions cost SGB, and it runs the same client, consensus and contract registry as Flare. In practice its role has narrowed sharply \u2014 it is protocol rehearsal rather than production, and the token is being delisted by exchanges.',
    testnet: false,
    specs: [
      { label: 'Consensus', value: 'Snowman++ (BFT, same client and consensus as Flare)' },
      { label: 'Block time', value: '1,200 ms cadence' },
      { label: 'Fees', value: 'SGB; no faucet \u2014 fund the network yourself' },
      { label: 'EVM', value: 'Ethereum-compatible addresses and EIP-2718 transactions' },
      { label: 'Chain TVL', value: '~$159K (Flare is ~$133M)' },
      { label: 'Role', value: 'Canary network \u2014 stage 2 of the Flare upgrade path' },
    ],
    sections: [
      {
        heading: 'What Songbird is for',
        body: [
          'Songbird is where Flare\u2019s protocol upgrades are rehearsed. Flare\u2019s development guidance sets out two tracks: application development goes from Coston2 directly to Flare Mainnet, while anything that changes the protocol must move through Songbird Testnet Coston, then Songbird Canary-Network, then Flare Testnet Coston2, and only then Flare Mainnet. Songbird is the second stage of that four-step path.',
          'That is why Songbird still matters operationally even though day-to-day dApp work has moved elsewhere. A protocol change is proven on Songbird before it is allowed near mainnet, so a chain with modest transaction counts is serving a purpose that transaction volume alone would not suggest.',
        ],
      },
      {
        heading: 'A winding-down chain, and what that means',
        body: [
          'Songbird is effectively in run-off. Its chain TVL is roughly $159,000 against Flare\u2019s $133M \u2014 around 840 times smaller \u2014 and of the nine protocols DefiLlama tracks with a Songbird deployment, all but two hold zero value. \u015bnosys AMM V3 (about $7.6M) and Pangolin V2 (about $2.2M) are the largest remaining deployments, and both predate Flare Mainnet; most activity has migrated.',
          'SGB is being actively delisted. CEX.IO and CoinSpot removed SGB in February 2026, and Bithumb suspended SGB deposits and withdrawals around the July 2026 network upgrade. There is no faucet on Songbird \u2014 Flare\u2019s own network configuration lists one as unavailable \u2014 so new developers must fund SGB themselves or build on Coston instead.',
          'Two things still run here that do not run on Flare: Flare Confidential Compute, for which Songbird is currently the only network, and FDC-V2 attestations, which ship on Songbird before Flare. Both are worth knowing, but FCC deployed without a final audit and runs on Google Confidential Compute operated by the Flare Foundation, which is a single-vendor bootstrap rather than a settled architecture.',
        ],
      },
      {
        heading: 'Shared protocols with Flare',
        body: [
          'Songbird and Flare share the client, the consensus protocol, the fee schedule and the FlareContractRegistry address, so the two are close to operationally identical. The Flare Time Series Oracle and Flare Data Connector are both available here. The difference is economic: Songbird transactions cost SGB, whose supply dynamics are separate from FLR.',
          'Songbird\u2019s native token wrapper is WNAT, which wraps SGB for use with DeFi protocols that expect ERC-20 semantics and to carry FTSO delegation weight (capped at 2.5% of supply).',
        ],
      },
      {
        heading: 'EVM compatibility',
        body: [
          'Songbird matches Ethereum on address space and transaction encoding: 20-byte ECDSA addresses, RLP encoding, and EIP-2718 typed transactions. A contract deployed on Songbird is deployed the same way it would be on Flare, which is what makes the canary-to-mainnet path mechanical rather than bespoke.',
        ],
      },
    ],
    links: [
      { label: 'Songbird Explorer', href: 'https://songbird-explorer.flare.network' },
      { label: 'Flare Developer Hub \u2014 Network', href: 'https://dev.flare.network/network/overview' },
      { label: 'Consensus', href: 'https://dev.flare.network/network/consensus' },
    ],
    marketplaceNote:
      'Songbird remains a supported listing chain, but treat it as run-off: liquidity is effectively one chain over, and the SGB delistings mean a buyer may struggle to exit into fiat. If you hold an item here and want to sell it, migrating to Flare Mainnet is the practical route \u2014 the Flare-native NFT marketplaces both ship one-click Songbird\u2192Flare migration. Price items for what they are worth on this chain, not for what they would fetch on Flare.',
  },
  {
    slug: 'flare',
    name: 'Flare',
    chainId: 14,
    weight: 9,
    tagline: 'EVM-compatible Layer 1 built for data-intensive, interoperable applications.',
    intro:
      'Flare is an EVM-compatible Layer 1 that ships data protocols as part of the protocol itself rather than as optional oracles. Its defining feature is that price feeds and cross-chain event attestation are enshrined in the Flare Systems Protocol, so they inherit the network\u2019s economic security instead of depending on a third-party oracle network. Transactions are paid in FLR.',
    testnet: false,
    specs: [
      { label: 'Consensus', value: 'Snowman++ (BFT, from Avalanche\u2019s Snow family)' },
      { label: 'Block time', value: '~1.8 s target; 1,200 ms cadence' },
      { label: 'Finality', value: 'Single-slot, probabilistic (Snowball confidence counters)' },
      { label: 'Fees', value: 'FLR; base fee 500 gwei, legacy Type0 and EIP-1559 Type2' },
      { label: 'EVM', value: 'Ethereum-compatible addresses, EIP-2718 typed transactions' },
      { label: 'Protocols', value: 'FTSO and FDC, enshrined in the Flare Systems Protocol' },
      { label: 'Data providers', value: '100, capped at 3.3% stake each' },
    ],
    sections: [
      {
        heading: 'Consensus',
        body: [
          'Flare runs Snowman++, a Byzantine fault tolerant protocol from Avalanche\u2019s Snow family, and the same client runs on both the P- and C-chains. Snowman++ adds a soft proposer mechanism on top of Snowman: proposer slots are selected deterministically by stake and block height, with up to six proposer windows at five seconds each. That stake-weighted proposer selection is the distinguishing part \u2014 it is why Flare describes its consensus as Snowman++ rather than plain Snowman.',
          'Finality is probabilistic rather than deterministic. Flare\u2019s own wording is \u201cnear-instantaneous (single-slot) finality\u201d, achieved through Snowball\u2019s stake-weighted confidence counters (Beta) and topological ordering, with a finalisation threshold above 50%. In practice this is far quicker than probabilistic confirmation on chains like Ethereum, but it is a confidence threshold, not a guarantee \u2014 so a settlement on Flare is confirmed-on-receipt rather than absolute.',
          'Anyone can run a full node; validation is optional. Validators post proof-of-stake self-bond of at least 1,000,000 FLR, with a 60-day minimum validator commitment and a 14-day minimum delegation period. Flare does not publish a total validator count in its documentation; what it does publish is a fixed 100 data providers, each capped at 3.3% of stake.',
        ],
      },
      {
        heading: 'Enshrined data protocols',
        body: [
          'Flare\u2019s differentiator is that its data protocols are part of the protocol itself. The Flare Time Series Oracle (FTSO) provides decentralized price feeds, and the Flare Data Connector (FDC) verifies off-chain actions such as transactions on other blockchains. Both inherit the full economic security of the network because they are enforced by the protocol rather than by a set of independent operators.',
          'These two protocols are what make FAssets possible. FAsset minting relies on FTSO for decentralized pricing and FDC for verifying the lock event on the source chain, so a wrapped asset such as FXRP is backed by real collateral and verified source-chain events rather than by a multisig.',
        ],
      },
      {
        heading: 'FAssets',
        body: [
          'FAssets is Flare\u2019s trustless, over-collateralized bridge to networks that have no smart contracts. It creates wrapped representations of assets such as BTC, DOGE and XRP on Flare, backed by collateral and redeemable for the original asset. Because the source networks have no contract layer, backing is enforced through over-collateralization and redemption rather than through a contract on the source chain.',
          'Minting runs through the FAsset minting dApps at fasset.oracle-daemon.com and fassets.au.cc. Wallets documented as supporting FAsset minting include Bifrost, Ledger, Luminite and OxenFlow \u2014 Ledger for hardware custody, Bifrost and Luminite as multi-network options, and OxenFlow on iOS.',
        ],
      },
      {
        heading: 'Tokenomics',
        body: [
          'FLR pays every transaction fee on Flare, and all FLR spent as fees is burned. FDC fees for malformed or unconfirmable requests are permanently burned as well, and unclaimed inflation rewards are burned after three months. The burn address holds roughly 4.24 billion FLR.',
          'Inflation was reduced from 5% to a 3% target with a hard cap of 3 billion FLR per year under FIP.16, accepted in April 2026. Note that Flare\u2019s public supply figures are inconsistent across sources \u2014 on-chain totals around 100 billion FLR sit alongside a \u201c15 billion created\u201d figure in the FIP.01 distribution docs, because the docs distinguish created, distributed and inflationary supply. Around 85\u201386 billion FLR is circulating.',
          'About 19.8% of distributed supply \u2014 the Flare Foundation and Flare VC Fund \u2014 cannot vote, so voting power sits with the remaining 80.2%. Governance participation is low, and FIP.16 designates the Flare Foundation as the initial single block builder and administrator of the FIRE entity created to reinvest network earnings.',
          'FLR also serves as FAssets collateral alongside stablecoins and ETH, and its wrapped form WFLR carries delegation weight in the FTSO oracle \u2014 capped at 2.5% of WFLR supply to limit any single holder\u2019s influence.',
        ],
      },
      {
        heading: 'Ecosystem and fees in practice',
        body: [
          'Flare\u2019s chain TVL is roughly $133M. The largest protocols are Firelight Farm (about $76M, ERC-4626 FXRP vaults and the source of liquid-staked XRP), Kinetic (about $39M, lending), SparkDEX (about $18M, and Flare\u2019s own news calls it the highest-TVL protocol on the chain), Sceptre (about $16M, liquid staking) and Monarq (about $11M, vault risk curation). Enosys, the original flagship DEX, also runs a CDP-based loans product.',
          'FAssets launched on Flare Mainnet in September 2025 with FXRP, which is now the only FAsset live on mainnet \u2014 about 145 million FXRP outstanding. FBTC and FDOGE appear in the FAsset interface and roadmap but are **not** enabled on mainnet and have no published launch date, so \u201cFlare bridges Bitcoin\u201d is currently a roadmap claim rather than a shipped one.',
          'Gas is close to free: at a 500 gwei base fee a simple 21k-gas transfer costs roughly 0.01 FLR, well under a tenth of a cent. Non-gas protocol fees matter far more in practice \u2014 FDC attestations cost around 1 FLR (rising to 20 under FIP.16), and Web2Json attestations 100 FLR. There is no cleared-XRP or cUSD gas abstraction; FLR is the gas asset, though gasless USD\u208e0 and FXRP transfers are available through relayer and EIP-712 patterns.',
          'Two further cautions: FlareDatasets is dead \u2014 the domain no longer resolves and it has been removed from Flare\u2019s navigation, superseded by FDC and FTSOv2 \u2014 and the public RPC is pruned, so historical state queries need an archive node.',
        ],
      },
      {
        heading: 'Bridges and indexing',
        body: [
          'Beyond FAssets, Flare is connected by LayerZero V2, Stargate V2 and zkBridge, and carries omnichain fungible tokens including USD\u208e0, flrETH, USDC.e, WETH and USDT. FXRP is itself an omnichain fungible token with routes to Ethereum, Base, BNB Chain, Monad, Katana, HyperEVM and Hyperliquid, signed by four independent DVNs.',
          'Worth noting for anyone expecting a canonical FLR bridge: there is not one. FLR is the gas asset and moves by exchange rather than by bridge. flrETH is Dinero\u2019s liquid-staked ETH, not a FLR derivative.',
          'Indexing is supported by Envio, Goldsky, sqd and SubQuery, with Dune providing chain analytics. Wallet SDKs include Turnkey, Wagmi, MetaMask Embedded Wallets, RainbowKit and Etherspot.',
        ],
      },
    ],
    links: [
      { label: 'Flare Explorer', href: 'https://flare-explorer.flare.network' },
      { label: 'Flare Developer Hub', href: 'https://dev.flare.network/' },
      { label: 'Consensus', href: 'https://dev.flare.network/network/consensus' },
      { label: 'FAssets', href: 'https://dev.flare.network/fassets/overview' },
      { label: 'Developer tools', href: 'https://dev.flare.network/network/developer-tools' },
      { label: 'Systems Explorer', href: 'https://flare-systems-explorer.flare.network' },
    ],
    marketplaceNote:
      'Flare is the chain this marketplace was built on and carries the deepest catalogue. Prices are denominated in FLR; a completed sale settles through the AuctionManager contract on chain 14.',
  },
  {
    slug: 'coston',
    name: 'Coston',
    chainId: 16,
    tagline: 'The protocol-development testnet for the Flare ecosystem.',
    intro:
      'Coston is Flare\u2019s protocol testnet \u2014 the first hop in the four-network upgrade path that runs Songbird Testnet Coston, then Songbird Canary-Network, then Flare Testnet Coston2, and finally Flare Mainnet. It runs CFLR as its native currency and is where protocol-level changes are implemented before any of them reach a canary or production network.',
    testnet: true,
    specs: [
      { label: 'Consensus', value: 'Snowman++ (BFT, from Avalanche\u2019s Snow family)' },
      { label: 'Fees', value: 'CFLR' },
      { label: 'EVM', value: 'Ethereum-compatible addresses and typed transactions' },
      { label: 'Role', value: 'Protocol testnet \u2014 stage 1 of the Flare upgrade path' },
    ],
    sections: [
      {
        heading: 'Where Coston sits in the upgrade path',
        body: [
          'Flare ships four EVM-compatible, permissionless networks, and Coston is the protocol-development entry point. The documented path for a protocol change is Coston first, then Songbird Canary-Network, then Flare Testnet Coston2, then Flare Mainnet. Application developers do not need Coston at all \u2014 their path is Coston2 to Flare Mainnet.',
          'This distinction is the single most useful thing to know about Coston: it is not a general-purpose application testnet. Putting an application there means testing against a network that protocol work will reset.',
        ],
      },
      {
        heading: 'Testing against Coston',
        body: [
          'Because Coston is where protocol changes land first, code written against it is being tested against something that is still moving. Any integration built here should be expected to need revisiting as the change propagates to Songbird, Coston2 and Flare Mainnet.',
          'For day-to-day contract work, Coston2 (chain 114) is the network Flare recommends \u2014 its documentation describes Coston2 explicitly as the dApp development testnet.',
        ],
      },
    ],
    links: [
      { label: 'Coston Explorer', href: 'https://coston-explorer.flare.network' },
      { label: 'Coston Faucet (CFLR)', href: 'https://faucet.flare.network/coston' },
      { label: 'Flare Developer Hub \u2014 Network', href: 'https://dev.flare.network/network/overview' },
    ],
    marketplaceNote:
      'Coston is a protocol testnet with no real economic value in its native currency. Listings here are for development and integration testing; do not price them as if they were real assets.',
  },
  {
    slug: 'coston-2',
    name: 'Coston 2',
    chainId: 114,
    isTest: true,
    tagline: 'The dApp development testnet for Flare applications.',
    intro:
      'Coston2 is Flare\u2019s application-development testnet and the network Flare recommends for dApp work. It runs C2FLR as its native currency and carries the same enshrined data protocols as Flare Mainnet, including the Flare Time Series Oracle and the Flare Data Connector, so an integration built here behaves the way it will in production. A faucet provides C2FLR, FXRP and USDT0.',
    testnet: true,
    specs: [
      { label: 'Consensus', value: 'Snowman++ (BFT, from Avalanche\u2019s Snow family)' },
      { label: 'Fees', value: 'C2FLR' },
      { label: 'Protocols', value: 'FTSO and FDC, matching mainnet' },
      { label: 'Role', value: 'Application development testnet \u2014 stage 3 of the Flare upgrade path' },
    ],
    sections: [
      {
        heading: 'The application-development network',
        body: [
          'Flare distinguishes between two development tracks, and Coston2 is the one for application developers. An application is built on Coston2 and promoted to Flare Mainnet. The four-network protocol path that includes Songbird is for changes to the protocol itself and is not needed for an application.',
          'This makes Coston2 the right place to exercise listing, auction and settlement logic before it touches mainnet. C2FLR from the faucet is enough to simulate real fee behaviour, because fee mechanics match mainnet.',
        ],
      },
      {
        heading: 'Protocol parity with mainnet',
        body: [
          'Coston2 carries the same enshrined protocols as Flare Mainnet. The Flare Time Series Oracle and the Flare Data Connector are both available, so an integration depending on a price feed or on cross-chain event attestation can be tested here in the form it will ship.',
          'The Coston2 faucet distributes C2FLR along with FXRP and USDT0, so a flow touching a stablecoin or a wrapped XRP can be exercised end to end without sourcing those assets separately.',
        ],
      },
    ],
    links: [
      { label: 'Coston2 Explorer', href: 'https://coston2-explorer.flare.network' },
      { label: 'Flare Developer Hub \u2014 Home', href: 'https://dev.flare.network/' },
      { label: 'Network configuration', href: 'https://dev.flare.network/network/overview' },
    ],
    marketplaceNote:
      'The recommended pre-production environment for anything shipping to Flare Mainnet. Listings behave like mainnet listings but cost nothing to transact on.',
  },
  {
    slug: 'xdc',
    name: 'XDC Network',
    chainId: 50,
    tagline: 'Hybrid EVM-compatible Layer 1 built for trade finance and enterprise interoperability.',
    intro:
      'XDC Network is a Layer-1, EVM-compatible blockchain with a deliberately hybrid design: a public mainnet for open participation, plus permissioned enterprise subnets whose sensitive data stays private while hashed proofs are anchored to the public chain. Its consensus, XDPoS 2.0, is a delegated proof of stake with BFT finality and forensic monitoring. XDC positions itself around trade finance, tokenisation and regulated financial infrastructure.',
    testnet: false,
    specs: [
      { label: 'Consensus', value: 'XDPoS 2.0 \u2014 delegated PoS with BFT finality' },
      { label: 'Block time', value: '~2 seconds' },
      { label: 'Epoch length', value: '900 blocks' },
      { label: 'Validators', value: '108 masternodes, elected by stake' },
      { label: 'EVM version', value: 'Shanghai' },
      { label: 'Throughput', value: '2000+ TPS claimed by the project' },
      { label: 'Fees', value: 'XDC; under $0.0001 claimed by the project' },
    ],
    sections: [
      {
        heading: 'Consensus',
        body: [
          'XDC uses XinFin Delegated Proof of Stake version 2.0. Token holders stake XDC to vote for masternode candidates, the top 108 candidates become active masternodes, and masternodes take turns proposing blocks in a round-robin fashion. Validators sign to confirm, and rewards go to both masternodes and the voters who backed them.',
          'Finality is Byzantine fault tolerant, so a transaction is settled once sufficient masternodes have signed and does not need to wait out probabilistic confirmation windows. XDPoS 2.0 adds forensic monitoring, which detects and records malicious validator behaviour, and slashing, which penalises it. Validator sets rotate on a 900-block epoch cycle.',
        ],
      },
      {
        heading: 'Hybrid public and private architecture',
        body: [
          'The defining architectural choice on XDC is that public and permissioned states coexist. The public layer is the shared, immutable mainnet ledger with global validator participation and transparent verification. Layer-2 enterprise subnets carry restricted access and keep sensitive data private, anchoring only hashed proofs or checkpoints to the public chain.',
          'That delivers data privacy and auditability at the same time, which is the requirement that makes the chain interesting for regulated industries. The interoperability layer aims at the same end: ISO 20022 financial messaging, the R3 Corda ecosystem, and connections to traditional banking and trade systems. Subnet state is anchored to mainnet through a checkpoint contract.',
        ],
      },
      {
        heading: 'Performance',
        body: [
          'XDC publishes roughly 2-second block time, near-instant BFT finality, 2000+ TPS claimed throughput and fees under $0.0001. These are the project\u2019s own published figures.',
          'For a marketplace the practical consequence is that listing, bidding and settlement are cheap enough that transaction cost is rarely the reason a seller would hesitate.',
        ],
      },
    ],
    links: [
      { label: 'XDC Explorer (XDCScan)', href: 'https://xdcscan.com/' },
      { label: 'XDC Documentation', href: 'https://docs.xdc.network/' },
      { label: 'XDC Architecture', href: 'https://docs.xdc.network/learn/xdc-architecture/' },
      { label: 'XDPoS 2.0', href: 'https://docs.xdc.network/xdcchain/xdpos2/' },
    ],
    marketplaceNote:
      'XDC Mainnet is a supported listing chain here. Prices are denominated in XDC; a completed sale settles through the AuctionManager contract on chain 50.',
  },
  {
    slug: 'apothem',
    name: 'Apothem',
    chainId: 51,
    tagline: 'The testnet for XDC Network, where contract and protocol work happens first.',
    intro:
      'Apothem is XDC Network\u2019s testnet, running chain id 51 with TXDC as its native currency. It mirrors mainnet\u2019s XDPoS 2.0 consensus and roughly 2-second block time, so contracts behave the way they will on chain 50. XDC\u2019s documentation lists its RPC as erpc.apothem.network and its explorer as testnet.xdcscan.com, with a faucet for TXDC.',
    testnet: true,
    specs: [
      { label: 'Consensus', value: 'XDPoS 2.0 \u2014 delegated PoS with BFT finality' },
      { label: 'Block time', value: '~2 seconds' },
      { label: 'Epoch length', value: '900 blocks' },
      { label: 'Fees', value: 'TXDC (test token, no value)' },
      { label: 'EVM version', value: 'Shanghai' },
    ],
    sections: [
      {
        heading: 'Consensus and parameters',
        body: [
          'Apothem runs the same XDPoS 2.0 consensus as mainnet: delegated proof of stake with BFT finality, a rotating masternode set and 900-block epochs. The difference from mainnet is economic and operational \u2014 TXDC is a test token with no value, so transactions cost nothing in real terms.',
        ],
      },
      {
        heading: 'Testing against Apothem',
        body: [
          'Because consensus and block time match mainnet, timing-sensitive logic behaves the same way here as it will on chain 50. A contract that settles a listing against an auction end time can be exercised against real block arrival rather than simulated intervals.',
        ],
      },
    ],
    links: [
      { label: 'Apothem Explorer', href: 'https://testnet.xdcscan.com/' },
      { label: 'Coston2 Faucet', href: 'https://faucet.flare.network/coston2' },
      { label: 'XDC Documentation', href: 'https://docs.xdc.network/' },
      { label: 'XDC Architecture', href: 'https://docs.xdc.network/learn/xdc-architecture/' },
    ],
    marketplaceNote:
      'Apothem is XDC\u2019s testnet and TXDC has no real value. Listings here are for development and testing only.',
  },
  {
    slug: 'avalanche',
    name: 'Avalanche',
    chainId: 43114,
    tagline: 'Heterogeneous network of blockchains with sub-second finality.',
    intro:
      'Avalanche is a heterogeneous network rather than a single chain: the Primary Network runs three separate blockchains, and Avalanche L1s can be created alongside it for specific applications. The C-Chain is the EVM chain \u2014 chain id 43114, native token AVAX, running the Coreth virtual machine with a Geth-compatible API. This marketplace lists NFTs on the C-Chain, which is the part of Avalanche that behaves like an Ethereum chain.',
    testnet: false,
    specs: [
      { label: 'Consensus', value: 'Snow consensus family (same family Flare derives from)' },
      { label: 'Validator minimum stake', value: '2,000 AVAX' },
      { label: 'Delegation minimum', value: '25 AVAX' },
      { label: 'Staking duration', value: '2 weeks minimum, 1 year maximum' },
      { label: 'Supply', value: 'Capped at 720 million AVAX' },
      { label: 'EVM', value: 'Coreth; Geth-compatible API; Solidity' },
    ],
    sections: [
      {
        heading: 'Three chains, one network',
        body: [
          'The Primary Network runs three blockchains. The C-Chain is the contract chain: an EVM implementation running Coreth with a Geth-compatible API, where Solidity contracts live and where this marketplace\u2019s listings settle. The P-Chain handles validator and Avalanche L1-level operations, including staking and L1 creation. The X-Chain handles Avalanche Native Tokens \u2014 smart assets representing real-world value rather than ERC-20s.',
          'Separating these concerns is what makes the network heterogeneous: applications needing EVM semantics use the C-Chain, while asset-issuance and staking logic lives elsewhere. Avalanche Mainnet as a whole comprises the Primary Network plus every deployed Avalanche L1.',
        ],
      },
      {
        heading: 'Validation and staking',
        body: [
          'A node becomes a validator for the Primary Network by staking at least 2,000 AVAX. Validation and delegation are distinct: 25 AVAX is the minimum delegation, with staking durations from two weeks to one year. AVAX represents the weight each validator carries in consensus, so staking is a security role rather than only an investment.',
        ],
      },
      {
        heading: 'Tokenomics',
        body: [
          'AVAX has a capped supply of up to 720 million. The closer issuance gets to that cap, the lower the emission rate becomes, and fees are burned independently \u2014 which is what keeps the actual token count from ever reaching 720 million. AVAX pays fees on all three chains.',
        ],
      },
    ],
    links: [
      { label: 'Snowscan (C-Chain Explorer)', href: 'https://snowtrace.io/' },
      { label: 'Avalanche Builder Hub', href: 'https://build.avax.network/docs/primary-network' },
      { label: 'Tokenomics FAQ', href: 'https://support.avax.network/en/articles/6912428-tokenomics-faq' },
    ],
    marketplaceNote:
      'C-Chain listings are priced in AVAX and settle through the AuctionManager contract on chain 43114. Use Snowscan to verify a settlement receipt.',
  },
  {
    slug: 'monad',
    name: 'Monad',
    chainId: 143,
    weight: 10,
    tagline: 'EVM-compatible Layer 1 built for high throughput at low cost.',
    intro:
      'Monad is an Ethereum-compatible Layer 1 that produces a block every 300 ms and reaches full finality in 600 ms, with capacity for more than 10,000 transactions per second. Its consensus, MonadBFT, reaches speculative finality after one slot and full finality after two. MON is the native token and pays all gas fees.',
    testnet: false,
    specs: [
      { label: 'Consensus', value: 'MonadBFT (Byzantine fault tolerant)' },
      { label: 'Block time', value: '300 ms (observed on mainnet)' },
      { label: 'Finality', value: '300 ms speculative (1 slot), 600 ms full (2 slots)' },
      { label: 'Throughput', value: '10,000+ TPS capacity' },
      { label: 'Block gas budget', value: '150M gas (500M gas/s)' },
      { label: 'Per-transaction gas limit', value: '30M gas' },
      { label: 'Fees', value: 'MON, gas only' },
      { label: 'EVM', value: 'Ethereum-compatible; EIP-7702 supported' },
    ],
    sections: [
      {
        heading: 'Consensus',
        body: [
          'Monad uses MonadBFT, a Byzantine fault tolerant consensus protocol. The practical property that matters for a marketplace is deterministic finality: once a transaction is in a confirmed block it is settled, rather than being subject to probabilistic reorg risk. That is what makes it reasonable to treat an auction settlement as final on receipt.',
          'MonadBFT is designed so the chain can keep producing blocks quickly while a relatively small validator set agrees on them, rather than relying on a very large validator set for throughput.',
        ],
      },
      {
        heading: 'Execution and throughput',
        body: [
          'Monad produces a block every 300 ms and reaches full finality in 600 ms. Its documented capacity is more than 10,000 transactions per second, against a block gas budget of 150M gas (500M gas per second) and a per-transaction gas limit of 30M.',
          'These figures are worth reading with their stated basis. The throughput number is design capacity for typical transactions rather than a reading of current demand, so the rate seen in practice depends on how much gas each transaction uses. Block time is observed on mainnet, where the mean was about 302 ms in September 2026. Finality is a protocol figure: a block reaches speculative finality after one slot and full finality after two.',
          'Monad states these numbers are reached while preserving decentralization rather than trading it away \u2014 by optimizing the execution and storage layers instead of centralizing the validator set or requiring large amounts of RAM per node. Ethereum\u2019s equivalent limits are much lower (2.5M gas per second), set conservatively for reasons that include inefficient storage access patterns and single-threaded execution.',
          'For a marketplace, the practical effect is that transaction fees stay low while throughput is high, which is the combination that benefits most from many small transactions \u2014 listings, bids, transfers.',
        ],
      },
      {
        heading: 'EVM compatibility',
        body: [
          'Monad is an Ethereum-compatible Layer 1, so Solidity contracts and standard Ethereum tooling carry over with limited modification. Its documentation sets out the specific differences from Ethereum separately, which is the honest way to think about compatibility: the common path is unmodified, the edges need checking.',
          'EIP-7702 is supported with the same workflow as on Ethereum \u2014 users sign a message authorizing an EOA to delegate to a specific contract, and that delegation can be revoked. Monad documents two Monad-specific constraints: a delegated EOA cannot dip below a 10 MON balance, and delegated contract code cannot call CREATE or CREATE2.',
          'Monad also uses opcode-level gas pricing and ships precompiled contracts, both of which are documented as deliberate differences from Ethereum rather than bugs to work around.',
        ],
      },
      {
        heading: 'Bridges and token supply',
        body: [
          'Assets issued natively on another chain reach Monad through several bridges, including LayerZero OFT, Chainlink CCIP, Hyperlane, and the 2/2 NTT Bridge \u2014 typically designated by the asset issuer rather than chosen by the user.',
          'MON is the native token and is used to pay for gas. The Monad Foundation funded the project through equity and SAFT-style vehicles rather than a public token sale, and retains a substantial share of early supply; there was no premine and no ICO.',
        ],
        links: [
          { label: 'Network information (mainnet)', href: 'https://docs.monad.xyz/developer-essentials/network-information' },
          { label: 'Differences between Monad and Ethereum', href: 'https://docs.monad.xyz/developer-essentials/differences' },
          { label: 'EIP-7702 on Monad', href: 'https://docs.monad.xyz/developer-essentials/eip-7702' },
          { label: 'Tokens and bridges', href: 'https://docs.monad.xyz/developer-essentials/network-information/tokens-and-bridges' },
        ],
      },
    ],
    links: [
      { label: 'Monad Explorer (MonadScan)', href: 'https://monadscan.com/' },
      { label: 'Monad Documentation', href: 'https://docs.monad.xyz/' },
      { label: 'Monad Mainnet RPC', href: 'https://rpc.monad.xyz' },
    ],
    marketplaceNote:
      'Monad Mainnet is a supported listing chain. Prices are denominated in MON; low transaction fees make it inexpensive to list, bid and settle.',
  },
  {
    slug: 'monad-testnet',
    name: 'Monad Testnet',
    chainId: 10143,
    isTest: true,
    tagline: 'The testnet for the Monad network.',
    intro:
      'Monad Testnet is the pre-production network for Monad, running chain id 10143 with MON as its native token. It mirrors mainnet\u2019s MonadBFT consensus and EVM-equivalent execution, so contracts and integrations can be exercised against realistic behaviour before deploying to chain 143. Test MON is available from a faucet.',
    testnet: true,
    specs: [
      { label: 'Consensus', value: 'MonadBFT (Byzantine fault tolerant)' },
      { label: 'Fees', value: 'MON (test token, no value)' },
      { label: 'EVM', value: 'EVM-compatible, mirrors mainnet' },
    ],
    sections: [
      {
        heading: 'Testing against Monad Testnet',
        body: [
          'Because the testnet runs the same consensus and execution model as mainnet, a contract exercised here behaves the way it will on chain 143. Timing-sensitive marketplace logic \u2014 auction end times, settlement deadlines, cancellation windows \u2014 can be tested against real block arrival.',
          'Test MON comes from the faucet, and the testnet is the network to deploy to before anything is promoted to mainnet.',
        ],
      },
    ],
    links: [
      { label: 'Monad Testnet Explorer', href: 'https://testnet.monadvision.com/' },
      { label: 'Monad Documentation', href: 'https://docs.monad.xyz/' },
      { label: 'Monad Testnet RPC', href: 'https://testnet-rpc.monad.xyz' },
    ],
    marketplaceNote:
      'Monad Testnet listings are for development and integration testing only. Test MON has no value.',
  },
]

/**
 * SEO page title for a chain, e.g. "Flare Network | VIRI".
 *
 * Chains whose display name already ends in "Network" or "Testnet" are used
 * as-is, so the title never reads "XDC Network Network".
 */
export function chainPageTitle(article: ChainArticle): string {
  const needsSuffix = !/\b(Network|Testnet)$/.test(article.name)
  return `${article.name}${needsSuffix ? ' Network' : ''} | VIRI`
}

/** Find an article by its route slug. */
export function chainArticleBySlug(slug: string): ChainArticle | undefined {
  return CHAIN_ARTICLES.find((a) => a.slug === slug)
}